// A brand's folders as live's project tree (spec A3b): the API lists them flat, each with its
// `parent_id`, and every screen that shows the tree (the library's panel, the pickers, the
// breadcrumbs) reads it through these helpers, so the tree is built one way.
import type { Folder } from "../../api/generated/model";

export interface FolderTree {
  /** Each folder by id. */
  byId: ReadonlyMap<string, Folder>;
  /** The folders under a parent, by name; `null` is the brand's top level. */
  children: (parentId: string | null) => readonly Folder[];
}

const byName = (a: Folder, b: Folder) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: "base" });

/** The tree of a brand's flat folder list. A folder whose parent is not in the list (a stale
 *  read) shows at the top level rather than nowhere. */
export function folderTree(folders: readonly Folder[]): FolderTree {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const groups = new Map<string | null, Folder[]>();
  for (const folder of folders) {
    const parent =
      folder.parent_id !== null && byId.has(folder.parent_id) ? folder.parent_id : null;
    const group = groups.get(parent) ?? [];
    group.push(folder);
    groups.set(parent, group);
  }
  for (const group of groups.values()) group.sort(byName);
  return { byId, children: (parentId) => groups.get(parentId) ?? [] };
}

/** Every folder under `folderId`, at any depth (not the folder itself). */
export function descendantIds(tree: FolderTree, folderId: string): string[] {
  const out: string[] = [];
  // The API refuses a cycle; a stale read must still never loop.
  const seen = new Set([folderId]);
  const walk = (id: string) => {
    for (const child of tree.children(id)) {
      if (seen.has(child.id)) continue;
      seen.add(child.id);
      out.push(child.id);
      walk(child.id);
    }
  };
  walk(folderId);
  return out;
}

/** The folders a move or a delete's target may not be: the folder and everything under it
 *  (live's `disabledIds`; the API answers 422 for them too). */
export function selfAndDescendants(tree: FolderTree, folderId: string): string[] {
  return [folderId, ...descendantIds(tree, folderId)];
}

/** The folders from the top level down to `folderId`, both included; empty when it is unknown. */
export function folderPath(tree: FolderTree, folderId: string | undefined): Folder[] {
  const path: Folder[] = [];
  const seen = new Set<string>();
  let current = folderId === undefined ? undefined : tree.byId.get(folderId);
  while (current !== undefined && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    current = current.parent_id === null ? undefined : tree.byId.get(current.parent_id);
  }
  return path;
}

/** The folders whose name holds `text` (any case), with every folder above them, so a search
 *  shows its hits in place in the tree; `undefined` when there is no search. */
export function searchedIds(tree: FolderTree, text: string): ReadonlySet<string> | undefined {
  const needle = text.trim().toLowerCase();
  if (needle === "") return undefined;
  const shown = new Set<string>();
  for (const folder of tree.byId.values()) {
    if (!folder.name.toLowerCase().includes(needle)) continue;
    for (const above of folderPath(tree, folder.id)) shown.add(above.id);
  }
  return shown;
}
